import React, { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import { Row, Col, Button, Container, Alert } from 'react-bootstrap';
import '../components/Dropdowns.css';
import '../fontawesome.css';
import '../solid.css';
import DataDropdown from '../components/DataDropdown';
import UserTable from '../components/UserTable';
import AddUsersModal from '../components/AddUsersModal';
import LoadingSpinner from '../components/LoadingSpinner';
import { userManagementAPI } from '../utils/apiClient';
import { useAuth } from "react-oidc-context";
import { useUser } from "../context/UserContext";

function UserManagement() {
  useEffect(() => {
    document.title = 'Search Console User Management - LostLogic';
  }, []);
  const auth = useAuth();
  const { orgHash } = useUser();
  
  // Dropdown options from API
  const [emails, setEmails] = useState([]);
  const [domains, setDomains] = useState([]);
  const [properties, setProperties] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [dates, setDates] = useState([]);
  
  // Selected values
  const [emailsOption, setEmailsOption] = useState('');
  const [domainOption, setDomainOption] = useState('');
  const [gscPropOption, setGscPropOption] = useState('');
  const [permissionsOption, setPermissionsOption] = useState('');
  const [dateOption, setDateOption] = useState('');
  
  // Table data
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  
  // Loading and error states
  const [loadingMetadata, setLoadingMetadata] = useState(true);
  const [loadingTableData, setLoadingTableData] = useState(false);
  const [error, setError] = useState(null);

  // Add Users Modal state
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);

  // Fetch metadata on component mount
  useEffect(() => {
    const fetchMetadata = async () => {
      const token = auth.user?.access_token;
      if (!token) {
        setError('Authentication token not found');
        setLoadingMetadata(false);
        return;
      }

      if (!orgHash) {
        console.log('Waiting for orgHash...');
        return;
      }

      try {
        setLoadingMetadata(true);
        setError(null);
        
        console.log('Fetching metadata with orgHash:', orgHash);
        
        const metadata = await userManagementAPI.getMetadata(token, null, orgHash);
        
        console.log('Metadata received:', metadata);
        
        setEmails(metadata.dropdowns.emails || []);
        setDomains(metadata.dropdowns.domains || []);
        setProperties(metadata.dropdowns.properties || []);
        setPermissions(metadata.dropdowns.permissions || []);
        setDates(metadata.dates || []);
        
        const latestDate = metadata.date;
        setDateOption(latestDate);
        
        if (latestDate) {
          await fetchTableData(token, latestDate);
        }
        
      } catch (err) {
        console.error('Error fetching metadata:', err);
        setError('Failed to load metadata: ' + err.message);
      } finally {
        setLoadingMetadata(false);
      }
    };

    fetchMetadata();
  }, [auth.user, orgHash]);

  useEffect(() => {
    const token = auth.user?.access_token;
    if (dateOption && token && !loadingMetadata && orgHash) {
      fetchTableData(token, dateOption);
      fetchMetadataForDate(token, dateOption);
    }
  }, [dateOption, orgHash]);

  useEffect(() => {
    applyFilters();
  }, [tableData, emailsOption, domainOption, gscPropOption, permissionsOption]);

  const fetchMetadataForDate = async (token, date) => {
    try {
      const metadata = await userManagementAPI.getMetadata(token, date, orgHash);
      
      setEmails(metadata.dropdowns.emails || []);
      setDomains(metadata.dropdowns.domains || []);
      setProperties(metadata.dropdowns.properties || []);
      setPermissions(metadata.dropdowns.permissions || []);
      
    } catch (err) {
      console.error('Error fetching metadata for date:', err);
    }
  };

  const fetchTableData = async (token, date) => {
    try {
      setLoadingTableData(true);
      setError(null);
      
      console.log('DEBUG fetchTableData - date:', date, 'orgHash:', orgHash);
      const response = await userManagementAPI.getUserData(token, date, orgHash);
      
      console.log('Table data received:', response);
      
      setTableData(response.data || []);
      setFilteredData(response.data || []);
      
    } catch (err) {
      console.error('Error fetching table data:', err);
      setError('Failed to load table data: ' + err.message);
      setTableData([]);
      setFilteredData([]);
    } finally {
      setLoadingTableData(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tableData];
    
    if (emailsOption) {
      filtered = filtered.filter(row => row.email === emailsOption);
    }
    
    if (domainOption) {
      filtered = filtered.filter(row => row.domain === domainOption);
    }
    
    if (gscPropOption) {
      filtered = filtered.filter(row => row.property === gscPropOption);
    }
    
    if (permissionsOption) {
      filtered = filtered.filter(row => row.permissions === permissionsOption);
    }
    
    console.log('Filters applied. Showing', filtered.length, 'of', tableData.length, 'rows');
    setFilteredData(filtered);
  };

  const handleClearFilters = () => {
    setEmailsOption('');
    setDomainOption('');
    setGscPropOption('');
    setPermissionsOption('');
  };

  const handleSubmit = () => {
    console.log('Current filters:', {
      email: emailsOption,
      domain: domainOption,
      property: gscPropOption,
      permissions: permissionsOption,
      date: dateOption
    });
  };

  return (
    <>
      <Container fluid style={{ paddingLeft: '15px', paddingRight: '15px' }}>
        <h1>User Management</h1>
        
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        
        <Row >
          <Col xs={12} lg={10}>
            <Form className="d-flex flex-wrap align-items-center" id="list-user-filters">
              <DataDropdown 
                setSelectedProperty={setEmailsOption} 
                data={emails} 
                placeholder="Email" 
                inputID="emailSelector" 
                inputName="emailProperties" 
                className="m-1" 
                zIndex="10"
                value={emailsOption}
                disabled={loadingMetadata}
              />
              <DataDropdown 
                setSelectedProperty={setDomainOption} 
                data={domains} 
                placeholder="GSC Domain" 
                inputID="domainSelector" 
                inputName="domainProperties" 
                className="m-1" 
                zIndex="9"
                value={domainOption}
                disabled={loadingMetadata}
              />
              <DataDropdown 
                setSelectedProperty={setGscPropOption} 
                data={properties} 
                placeholder="GSC Property" 
                inputID="gscPropSelector" 
                inputName="gscPropProperties" 
                className="m-1" 
                zIndex="8"
                value={gscPropOption}
                disabled={loadingMetadata}
              />
              <DataDropdown 
                setSelectedProperty={setPermissionsOption} 
                data={permissions} 
                placeholder="Permissions" 
                inputID="permissionsSelector" 
                inputName="permissionsProperties" 
                className="m-1" 
                zIndex="7"
                value={permissionsOption}
                disabled={loadingMetadata}
              />
              <Button 
                className="rounded m-1" 
                variant="danger" 
                style={{height: '35px', display: 'flex', alignItems: 'center', paddingLeft: '12px', paddingRight: '12px'}} 
                id="clearButton" 
                onClick={handleClearFilters}
                disabled={loadingMetadata}
              >
                <i className="fas fa-eraser"></i>
              </Button>
            </Form>
          </Col>
          <Col xs={12} lg={2} className="d-flex justify-content-lg-end align-items-center">
            <Form>
              <DataDropdown 
                setSelectedProperty={setDateOption} 
                data={dates} 
                placeholder="Date" 
                inputID="list-user-dateSelector" 
                inputName="dateProperties"
                zIndex="6"
                value={dateOption}
                disabled={loadingMetadata}
              />
            </Form>
          </Col>
        </Row>
        
        {!loadingTableData && tableData.length > 0 && (
          <Row className="mt-3 mb-2 align-items-center">
            <Col xs={12} md={6}></Col>
            <Col xs={12} md={6} className="d-flex justify-content-md-end mt-2 mt-md-0">
              <Button 
                variant="success" 
                onClick={() => setShowAddUsersModal(true)}
                id = "list-user-button"
                style={{
                  backgroundColor: '#1abc9c',
                  borderColor: '#1abc9c',
                  fontWeight: '500'
                }}
              >
                <i className="fas fa-plus me-2"></i>
                Add Users
              </Button>
            </Col>
          </Row>
        )}
        
        {/* Show spinner inline in table area */}
        {(loadingTableData || loadingMetadata) && (
          <Row className="mt-5">
            <Col className="text-center">
              <LoadingSpinner message={loadingMetadata ? "Loading user management data..." : "Loading table data..."} />
            </Col>
          </Row>
        )}
      </Container>
      
      <Container fluid style={{ paddingLeft: '15px', paddingRight: '15px' }}>
        {!loadingTableData && !loadingMetadata && <UserTable data={filteredData} isLoading={false} />}
      </Container>

      {/* Add Users Modal */}
      <AddUsersModal 
        show={showAddUsersModal}
        onHide={() => setShowAddUsersModal(false)}
      />
    </>
  )
}

export default UserManagement;


// import React, { useState, useEffect } from 'react';
// import Form from 'react-bootstrap/Form';
// import { Row, Col, Button, Container, Alert, Spinner } from 'react-bootstrap';
// import '../components/Dropdowns.css';
// import '../fontawesome.css';
// import '../solid.css';
// import DataDropdown from '../components/DataDropdown';
// import UserTable from '../components/UserTable';
// import AddUsersModal from '../components/AddUsersModal';
// import { userManagementAPI } from '../utils/apiClient';
// import { useAuth } from "react-oidc-context";
// import { useUser } from "../context/UserContext";

// function UserManagement() {
//   useEffect(() => {
//     document.title = 'Search Console User Management - LostLogic';
//   }, []);
//   const auth = useAuth();
//   const { orgHash } = useUser();
  
//   // Dropdown options from API
//   const [emails, setEmails] = useState([]);
//   const [domains, setDomains] = useState([]);
//   const [properties, setProperties] = useState([]);
//   const [permissions, setPermissions] = useState([]);
//   const [dates, setDates] = useState([]);
  
//   // Selected values
//   const [emailsOption, setEmailsOption] = useState('');
//   const [domainOption, setDomainOption] = useState('');
//   const [gscPropOption, setGscPropOption] = useState('');
//   const [permissionsOption, setPermissionsOption] = useState('');
//   const [dateOption, setDateOption] = useState('');
  
//   // Table data
//   const [tableData, setTableData] = useState([]);
//   const [filteredData, setFilteredData] = useState([]);
  
//   // Loading and error states
//   const [loadingMetadata, setLoadingMetadata] = useState(true);
//   const [loadingTableData, setLoadingTableData] = useState(false);
//   const [error, setError] = useState(null);

//   // Add Users Modal state
//   const [showAddUsersModal, setShowAddUsersModal] = useState(false);

//   // Fetch metadata on component mount
//   useEffect(() => {
//     const fetchMetadata = async () => {
//       const token = auth.user?.access_token;
//       if (!token) {
//         setError('Authentication token not found');
//         setLoadingMetadata(false);
//         return;
//       }

//       if (!orgHash) {
//         console.log('Waiting for orgHash...');
//         return;
//       }

//       try {
//         setLoadingMetadata(true);
//         setError(null);
        
//         console.log('Fetching metadata with orgHash:', orgHash);
        
//         const metadata = await userManagementAPI.getMetadata(token, null, orgHash);
        
//         console.log('Metadata received:', metadata);
        
//         setEmails(metadata.dropdowns.emails || []);
//         setDomains(metadata.dropdowns.domains || []);
//         setProperties(metadata.dropdowns.properties || []);
//         setPermissions(metadata.dropdowns.permissions || []);
//         setDates(metadata.dates || []);
        
//         const latestDate = metadata.date;
//         setDateOption(latestDate);
        
//         if (latestDate) {
//           await fetchTableData(token, latestDate);
//         }
        
//       } catch (err) {
//         console.error('Error fetching metadata:', err);
//         setError('Failed to load metadata: ' + err.message);
//       } finally {
//         setLoadingMetadata(false);
//       }
//     };

//     fetchMetadata();
//   }, [auth.user, orgHash]);

//   useEffect(() => {
//     const token = auth.user?.access_token;
//     if (dateOption && token && !loadingMetadata && orgHash) {
//       fetchTableData(token, dateOption);
//       fetchMetadataForDate(token, dateOption);
//     }
//   }, [dateOption, orgHash]);

//   useEffect(() => {
//     applyFilters();
//   }, [tableData, emailsOption, domainOption, gscPropOption, permissionsOption]);

//   const fetchMetadataForDate = async (token, date) => {
//     try {
//       const metadata = await userManagementAPI.getMetadata(token, date, orgHash);
      
//       setEmails(metadata.dropdowns.emails || []);
//       setDomains(metadata.dropdowns.domains || []);
//       setProperties(metadata.dropdowns.properties || []);
//       setPermissions(metadata.dropdowns.permissions || []);
      
//     } catch (err) {
//       console.error('Error fetching metadata for date:', err);
//     }
//   };

//   const fetchTableData = async (token, date) => {
//     try {
//       setLoadingTableData(true);
//       setError(null);
      
//       console.log('DEBUG fetchTableData - date:', date, 'orgHash:', orgHash);
//       const response = await userManagementAPI.getUserData(token, date, orgHash);
      
//       console.log('Table data received:', response);
      
//       setTableData(response.data || []);
//       setFilteredData(response.data || []);
      
//     } catch (err) {
//       console.error('Error fetching table data:', err);
//       setError('Failed to load table data: ' + err.message);
//       setTableData([]);
//       setFilteredData([]);
//     } finally {
//       setLoadingTableData(false);
//     }
//   };

//   const applyFilters = () => {
//     let filtered = [...tableData];
    
//     if (emailsOption) {
//       filtered = filtered.filter(row => row.email === emailsOption);
//     }
    
//     if (domainOption) {
//       filtered = filtered.filter(row => row.domain === domainOption);
//     }
    
//     if (gscPropOption) {
//       filtered = filtered.filter(row => row.property === gscPropOption);
//     }
    
//     if (permissionsOption) {
//       filtered = filtered.filter(row => row.permissions === permissionsOption);
//     }
    
//     console.log('Filters applied. Showing', filtered.length, 'of', tableData.length, 'rows');
//     setFilteredData(filtered);
//   };

//   const handleClearFilters = () => {
//     setEmailsOption('');
//     setDomainOption('');
//     setGscPropOption('');
//     setPermissionsOption('');
//   };

//   const handleSubmit = () => {
//     console.log('Current filters:', {
//       email: emailsOption,
//       domain: domainOption,
//       property: gscPropOption,
//       permissions: permissionsOption,
//       date: dateOption
//     });
//   };

//   if (loadingMetadata) {
//     return (
//       <Container className="mt-5 text-center">
//         <Spinner animation="border" role="status">
//           <span className="visually-hidden">Loading...</span>
//         </Spinner>
//         <p className="mt-3">Loading user management data...</p>
//       </Container>
//     );
//   }

//   return (
//     <>
//       <Container fluid style={{ paddingLeft: '15px', paddingRight: '15px' }}>
//         <h1>User Management</h1>
        
//         {error && (
//           <Alert variant="danger" onClose={() => setError(null)} dismissible>
//             {error}
//           </Alert>
//         )}
        
//         <Row>
//           <Col xs={12} lg={10}>
//             <Form className="d-flex flex-wrap align-items-center">
//               <DataDropdown 
//                 setSelectedProperty={setEmailsOption} 
//                 data={emails} 
//                 placeholder="Email" 
//                 inputID="emailSelector" 
//                 inputName="emailProperties" 
//                 className="m-1" 
//                 zIndex="10"
//                 value={emailsOption}
//               />
//               <DataDropdown 
//                 setSelectedProperty={setDomainOption} 
//                 data={domains} 
//                 placeholder="GSC Domain" 
//                 inputID="domainSelector" 
//                 inputName="domainProperties" 
//                 className="m-1" 
//                 zIndex="9"
//                 value={domainOption}
//               />
//               <DataDropdown 
//                 setSelectedProperty={setGscPropOption} 
//                 data={properties} 
//                 placeholder="GSC Property" 
//                 inputID="gscPropSelector" 
//                 inputName="gscPropProperties" 
//                 className="m-1" 
//                 zIndex="8"
//                 value={gscPropOption}
//               />
//               <DataDropdown 
//                 setSelectedProperty={setPermissionsOption} 
//                 data={permissions} 
//                 placeholder="Permissions" 
//                 inputID="permissionsSelector" 
//                 inputName="permissionsProperties" 
//                 className="m-1" 
//                 zIndex="7"
//                 value={permissionsOption}
//               />
//               {/* <Button 
//                 className="rounded m-1" 
//                 variant="primary" 
//                 style={{height: '35px', display: 'flex', alignItems: 'center', paddingLeft: '12px', paddingRight: '12px'}} 
//                 id="filterButton" 
//                 onClick={handleSubmit}
//               >
//                 <i className="fas fa-paper-plane"/>
//               </Button> */}
//               <Button 
//                 className="rounded m-1" 
//                 variant="danger" 
//                 style={{height: '35px', display: 'flex', alignItems: 'center', paddingLeft: '12px', paddingRight: '12px'}} 
//                 id="clearButton" 
//                 onClick={handleClearFilters}
//               >
//                 <i className="fas fa-eraser"></i>
//               </Button>
//             </Form>
//           </Col>
//           <Col xs={12} lg={2} className="d-flex justify-content-lg-end align-items-center">
//             <Form>
//               <DataDropdown 
//                 setSelectedProperty={setDateOption} 
//                 data={dates} 
//                 placeholder="Date" 
//                 inputID="dateSelector" 
//                 inputName="dateProperties"
//                 zIndex="6"
//                 value={dateOption}
//               />
//             </Form>
//           </Col>
//         </Row>
        
//         {loadingTableData && (
//           <Row className="mt-3">
//             <Col className="text-center">
//               <Spinner animation="border" size="sm" /> Loading data...
//             </Col>
//           </Row>
//         )}
        
//         {!loadingTableData && tableData.length > 0 && (
//           <Row className="mt-3 mb-2 align-items-center">
//             <Col xs={12} md={6}></Col>
//             <Col xs={12} md={6} className="d-flex justify-content-md-end mt-2 mt-md-0">
//               <Button 
//                 variant="success" 
//                 onClick={() => setShowAddUsersModal(true)}
//                 style={{
//                   backgroundColor: '#1abc9c',
//                   borderColor: '#1abc9c',
//                   fontWeight: '500'
//                 }}
//               >
//                 <i className="fas fa-plus me-2"></i>
//                 Add Users
//               </Button>
//             </Col>
//           </Row>
//         )}
//       </Container>
      
//       <Container fluid style={{ paddingLeft: '15px', paddingRight: '15px' }}>
//         <UserTable data={filteredData} isLoading={loadingTableData} />
//       </Container>

//       {/* Add Users Modal */}
//       <AddUsersModal 
//         show={showAddUsersModal}
//         onHide={() => setShowAddUsersModal(false)}
//       />
//     </>
//   )
// }

// export default UserManagement;